'use client';

import { Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Card, CardContent } from '@kit/ui/card';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

import styles from './native-booking-controls.module.css';

interface BallMachineCardProps {
  machine: {
    id: string;
    name: string;
    picture_url?: string | null;
    normal_price?: number | null;
    peak_price?: number | null;
    applicable_price?: number | null; // Server-calculated price
  };
  isSelected: boolean;
  onSelect: () => void;
  isPeak?: boolean; // For display purposes only (showing peak badge)
}

export function BallMachineCard({
  machine,
  isSelected,
  onSelect,
  isPeak = false,
}: BallMachineCardProps) {
  const { t } = useTranslation();
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Use server-calculated applicable_price if available, otherwise fallback to normal_price
  const price = machine.applicable_price ?? machine.normal_price;

  return (
    <Card
      data-selected={isSelected}
      className={cn(
        styles.optionCard,
        'relative cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-primary ring-2',
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 shrink-0">
            <AvatarImage src={machine.picture_url || undefined} />
            <AvatarFallback>
              {machine.picture_url ? (
                getInitials(machine.name)
              ) : (
                <Target className="h-8 w-8" />
              )}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-lg font-semibold">{machine.name}</h3>
            </div>

            <div className="mt-3 flex items-center justify-between">
              {price !== null && price !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-lg font-semibold">
                    <CurrencyIcon className="h-4 w-4" /> {price.toFixed(2)}
                  </span>
                  {isPeak && (
                    <Badge variant="secondary" className="text-xs">
                      {t('bookings:peakBadge')}
                    </Badge>
                  )}
                </div>
              )}

              {isSelected && (
                <Badge variant="default" className="ms-auto">
                  {t('bookings:selected')}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
